import api from '@shared/api';
import { Trans } from 'react-i18next';
import i18next from 'i18next';
import { Notification } from '@components/shared';
import routes from '@root/routes';
import { LandlordBuilding } from '@root/types';
import { EVENTS, PARAMETERS, EVENT_TYPES } from '@root/tracking/constants';
import analyticsActions from './analytics';

type ApiResponse<T> = {
  id: string;
  buildingId: string;
  saved: T;
};

const saveAnalyticsEvent = {
  action: PARAMETERS.saveItem,
  eventType: EVENT_TYPES.track,
  event: EVENTS.saveInteraction,
  sourcePage: PARAMETERS.buildingPage,
} as const;

type ReceivedSavedBuildingAction = {
  type: 'RECEIVE_SAVED_BUILDING';
  payload: Omit<ApiResponse<boolean>, 'id'> & {
    meta: {
      analytics: typeof saveAnalyticsEvent;
    };
  };
};

const receiveSavedBuilding = (savedBuildingId: any): ReceivedSavedBuildingAction => ({
  type: 'RECEIVE_SAVED_BUILDING',
  payload: {
    buildingId: savedBuildingId,
    saved: true,
    meta: {
      analytics: {
        action: PARAMETERS.saveItem,
        eventType: EVENT_TYPES.track,
        event: EVENTS.saveInteraction,
        sourcePage: PARAMETERS.buildingPage,
      },
    },
  },
});

const displaySuccessfulNotification = logInAnalytics => {
  Notification.saveSuccess({
    title: i18next.t('building:saved'),
    /* eslint-disable react/jsx-no-literals */
    text: (
      <Trans i18nKey="viewInFavorites" ns="building">
        View it in your
        <a href="/saved/buildings" onClick={logInAnalytics}>
          saved buildings
        </a>
        .
      </Trans>
    ),
    /* eslint-enable react/jsx-no-literals */
  });
};

export const saveBuilding = (id: string, slug: string) => async dispatch => {
  const response = await api.post(routes.api.buildingSave(slug));

  if (response.ok) {
    dispatch(receiveSavedBuilding(id));
    displaySuccessfulNotification(() =>
      dispatch(analyticsActions.clickToSavedBuildingsFromToast()),
    );
  }
};

export const unsaveBuilding = (id: string, slug: string) => async dispatch => {
  const response = await api.delete(routes.api.buildingSave(slug));

  if (response.ok) {
    dispatch(receiveUnsavedBuilding(id));
  }
};

const unsaveAnalyticsEvent = {
  action: PARAMETERS.unsaveItem,
  eventType: EVENT_TYPES.track,
  event: EVENTS.saveInteraction,
  sourcePage: PARAMETERS.buildingPage,
} as const;

type ReceivedUnsavedBuildingAction = {
  type: 'RECEIVE_UNSAVED_BUILDING';
  payload: Omit<ApiResponse<boolean>, 'id'> & {
    meta: {
      analytics: typeof unsaveAnalyticsEvent;
    };
  };
};

const receiveUnsavedBuilding = (buildingId): ReceivedUnsavedBuildingAction => ({
  type: 'RECEIVE_UNSAVED_BUILDING',
  payload: {
    buildingId,
    saved: false,
    meta: {
      analytics: unsaveAnalyticsEvent,
    },
  },
});

type ReceiveSavedBuildingsAction = {
  type: 'RECEIVE_SAVED_BUILDINGS';
  payload: {
    buildings: LandlordBuilding[];
  };
};
const receiveSavedBuildings = (
  buildings: Array<LandlordBuilding>,
): ReceiveSavedBuildingsAction => ({
  type: 'RECEIVE_SAVED_BUILDINGS',
  payload: {
    buildings,
  },
});

export const fetchSavedBuildings = () => async dispatch => {
  const response = await api.fetch(routes.api.saveBuilding());

  if (response.ok) {
    const savedBuildings = await response.json();
    dispatch(receiveSavedBuildings(savedBuildings));
  }
};

export const savedBuildingsPageLoadedAction = {
  type: 'SAVED_BUILDINGS_PAGE_LOADED',
  payload: {
    meta: {
      analytics: {
        eventType: EVENT_TYPES.page,
        event: PARAMETERS.savedBuildingsPage,
      },
    },
  },
} as const;
type SavedBuildingsPageLoadedAction = typeof savedBuildingsPageLoadedAction;
export const savedBuildingsPageLoaded = () => savedBuildingsPageLoadedAction;

export const clickedToBuildingAction = {
  type: 'CLICKED_TO_BUILDING',
  payload: {
    meta: {
      analytics: {
        eventType: EVENT_TYPES.track,
        event: EVENTS.clickToPage,
        sourcePage: PARAMETERS.savedBuildingsPage,
        destination: PARAMETERS.buildingPage,
        action: PARAMETERS.clickSavedBuilding,
      },
    },
  },
} as const;
type ClickedToBuildingAction = typeof clickedToBuildingAction;

export type Actions =
  | ClickedToBuildingAction
  | SavedBuildingsPageLoadedAction
  | ReceiveSavedBuildingsAction
  | ReceivedUnsavedBuildingAction
  | ReceivedSavedBuildingAction;
